import type {
  QuestionRoutingRules,
  RoutingValidateResult,
} from '@/features/instruments/domain/routing'

import { formatInstrumentTimelineDisplay as formatCampaignInstrumentTimeline } from '@/features/campaigns/lib/campaign-week-timeline'

export const PUBLISH_SUCCESS_AUTO_REDIRECT_SECONDS = 15

/** @deprecated Prefer campaign start/end dates via formatInstrumentTimelineDisplay. */
export const DEFAULT_INSTRUMENT_TIMELINE_DAYS = 2

/** @deprecated Use formatInstrumentTimelineDisplay — kept for existing imports. */
export const DEFAULT_CAMPAIGN_TIMELINE_DAYS = 2

export const PUBLISH_INSTRUMENT_ERROR_TOAST =
  'Instrument could not be published. Please try again.'

/** Preview "Instrument Timeline" — same week math as Launching Screen. */
export function formatInstrumentTimelineDisplay(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): string {
  return formatCampaignInstrumentTimeline(startDate, endDate)
}

export function formatEctPreviewDisplay(totalSeconds: number): string {
  if (totalSeconds <= 0) return '0 mins'
  if (totalSeconds < 60) {
    return `${Math.round(totalSeconds)} secs`
  }
  const mins = Math.floor(totalSeconds / 60)
  const secs = totalSeconds % 60
  if (secs === 0) return `${mins} mins`
  return `${mins} mins ${secs} secs`
}

export function countRoutingRules(routingRules: readonly QuestionRoutingRules[]): {
  routingRuleCount: number
  skipRuleCount: number
} {
  let routingRuleCount = 0
  let skipRuleCount = 0
  for (const block of routingRules) {
    routingRuleCount += block.branchingRules?.length ?? 0
    skipRuleCount += block.skipRules?.length ?? 0
  }
  return { routingRuleCount, skipRuleCount }
}

export function routingRulesSummaryText(routingRuleCount: number): string {
  if (routingRuleCount === 0) return 'No routing rules configured'
  if (routingRuleCount === 1) return '1 rule configured'
  return `${routingRuleCount} rules configured`
}

export function skipRulesSummaryText(skipRuleCount: number): string {
  if (skipRuleCount === 0) return '0 skip rules'
  if (skipRuleCount === 1) return '1 skip rule'
  return `${skipRuleCount} skip rules`
}

export function hasRoutingValidationErrors(
  validation: RoutingValidateResult | null | undefined,
): boolean {
  return Boolean(validation && !validation.valid)
}

export function canPublishFromPreview(args: {
  routingValidation: RoutingValidateResult | null | undefined
  walletSufficient: boolean
  audienceCoversTarget: boolean
  rewardEnabled: boolean
  budgetConfigured: boolean
}): boolean {
  if (hasRoutingValidationErrors(args.routingValidation)) return false
  if (!args.budgetConfigured) return false
  if (!args.audienceCoversTarget) return false
  if (args.rewardEnabled && !args.walletSufficient) return false
  return true
}

export function publishDisabledReason(args: {
  routingValidation: RoutingValidateResult | null | undefined
  walletSufficient: boolean
  audienceCoversTarget: boolean
  rewardEnabled: boolean
  budgetConfigured: boolean
}): string | null {
  if (hasRoutingValidationErrors(args.routingValidation)) {
    return 'Please fix routing errors before publishing'
  }
  if (args.budgetConfigured && !args.audienceCoversTarget) {
    return 'Response target exceeds current matching audience. Please review Step 4.'
  }
  if (args.rewardEnabled && !args.walletSufficient) {
    return 'Insufficient wallet balance. Top up your wallet before publishing.'
  }
  return null
}

/** User-facing toast when POST publish fails (BFP-256). */
export function publishInstrumentErrorMessage(_error: unknown): string {
  return PUBLISH_INSTRUMENT_ERROR_TOAST
}
